/* ============================================================
   Beneos Battlemaps : Beneos Database client (thin-shop model).
   Shopify holds only a link key per product (custom.map_release_no
   for map packs, handle = DB key for creatures/spells/items).
   Everything rich (scene list, biomes, adventures, stats) is read
   live from the public Beneos Database and rendered client-side.

   Source of truth: https://www.beneos-database.com/data/  (CORS: *)
   ============================================================ */
(function () {
  'use strict';

  var DB_BASE = 'https://www.beneos-database.com/data/';
  var SOURCES = {
    battlemap: { url: DB_BASE + 'battlemaps/beneos_battlemaps_database.json', thumbs: DB_BASE + 'battlemaps/thumbnails/' },
    token:     { url: DB_BASE + 'tokens/beneos_tokens_database_v2.json',      thumbs: DB_BASE + 'tokens/thumbnails_v2/' },
    spell:     { url: DB_BASE + 'spells/beneos_spells_database.json',         thumbs: DB_BASE + 'spells/thumbnails/' },
    item:      { url: DB_BASE + 'items/beneos_items_database.json',           thumbs: DB_BASE + 'items/thumbnails/' },
    i18n:      { url: DB_BASE + 'common/beneos_i18n.json',                    thumbs: '' }
  };
  /* Until 2026-09-30 this cache ran on a clock alone: for one hour it did not
     even ask. A release published inside that hour did not exist for the
     customer, and reloading did not help because localStorage outlives it.
     Measured on three creatures that entered the catalog at 21:05 and were
     missing from the shop while four older ones from the same day showed.

     The server decides now, not the clock. GRACE only smooths fast navigation
     inside one session; after that the browser revalidates on every call. When
     nothing changed the origin answers 304 and the body costs zero bytes
     (measured 2026-09-30 against www.beneos-database.com: the file carries
     Last-Modified and max-age=7200, and a conditional GET returns 304 with an
     empty body). Worst case per customer and day: 288 conditional calls per
     kind, each of them empty. */
  var GRACE = 5 * 60 * 1000;   // no request inside this window, always ask after it
  var CACHE_PREFIX = 'beneos_db_';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* release number from a DB key, with pack-field fallback. Plan §62: pack is
     now a canonical long-slug (bm_<release_id>_<pretty>) or the 'NoSlug'
     sentinel. Legacy values like "48_Dourcrag..." still parse via the same
     split-on-_ rule. */
  function releaseOf(key, pack) {
    var n = parseInt(String(key).split('-')[0], 10);
    if (!isNaN(n)) return String(n);
    if (pack && pack !== 'NoSlug') {
      var s = String(pack).replace(/^bm_/, '');
      var p = parseInt(s.split('_')[0], 10);
      if (!isNaN(p)) return String(p);
    }
    return null;
  }

  function readCache(kind) {
    try {
      var raw = window.localStorage.getItem(CACHE_PREFIX + kind);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }
  var quotaWarned = false;
  function writeCache(kind, data, lastModified, loadedAt) {
    try {
      window.localStorage.setItem(CACHE_PREFIX + kind, JSON.stringify({
        ts: Date.now(), geladen: loadedAt || Date.now(), data: data, lm: lastModified || null
      }));
    } catch (e) {
      /* A full quota is realistic at 1.4 MB per kind. Silently swallowing it
         leaves the cache permanently empty, so GRACE and the stamp comparison
         never take effect and every page view reloads and reparses the whole
         catalog, with nothing anywhere saying so. */
      if (!quotaWarned) {
        quotaWarned = true;
        try { console.warn('BeneosDB: catalog cache could not be written (' + (e && e.name) + '). Every page view will reload the full catalog.'); } catch (e2) {}
      }
    }
  }

  /* Pure: decides what to do with a cache entry, so the rule is testable
     without fetch and without localStorage.
     'grace'  use the copy, ask nobody
     'ask'    revalidate, the answer decides
     'load'   nothing usable stored, load fully */
  function cacheDecision(cached, now) {
    if (!cached || !cached.data) return 'load';
    if ((now - (cached.ts || 0)) < GRACE) return 'grace';
    return 'ask';
  }

  /* Pure: the answer arrived. Same stamp means the parsed copy still holds and
     we save parsing 1.4 MB of JSON. `lm` missing means no proof, so parse. */
  function answerDecision(cached, lastModified) {
    if (!lastModified) return 'parse';
    if (!cached || !cached.data || cached.lm !== lastModified) return 'parse';
    return 'keep';
  }

  /* One in-flight request per kind. Two consumers on the same page (home.js
     asks for battlemap and token in both of its fallback blocks) would
     otherwise race, and the slower answer would write its older data over the
     newer one with a fresh timestamp. */
  var inFlight = {};

  /* returns a Promise of the DB content object for a kind */
  function loadDb(kind) {
    var src = SOURCES[kind];
    if (!src) return Promise.reject(new Error('unknown db kind: ' + kind));
    var cached = readCache(kind);
    if (cacheDecision(cached, Date.now()) === 'grace') return Promise.resolve(cached.data);
    if (inFlight[kind]) return inFlight[kind];

    /* `cache: no-cache` makes the BROWSER revalidate with its own validator.
       That is the only workable route here: setting If-Modified-Since by hand
       is a non-safelisted header and triggers a CORS preflight that
       www.beneos-database.com REJECTS. Measured 2026-09-30:
       access-control-allow-headers lists only origin, x-requested-with and
       content-type, so the request would be blocked for every customer.
       Last-Modified is a CORS-safelisted response header and stays readable. */
    var p = fetch(src.url, { method: 'GET', cache: 'no-cache', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        var lm = r.headers.get('Last-Modified');
        if (answerDecision(cached, lm) === 'keep') {
          /* Refresh ts (last confirmation) but NOT `geladen`: otherwise a copy
             that is merely confirmed daily would look forever young. */
          writeCache(kind, cached.data, cached.lm, cached.geladen);
          return cached.data;
        }
        return r.json().then(function (json) {
          var content = json.content || json;
          writeCache(kind, content, lm, Date.now());
          return content;
        });
      })
      .catch(function (err) {
        /* Offline: an old catalog beats none. It is deliberately NOT capped by
           age: the consumers turn a rejection into an empty result
           (beneos-search.js catches into {}), and an empty shop that looks like
           a real answer is worse than an old one. */
        if (cached && cached.data) return cached.data;
        throw err;
      })
      .then(function (v) { delete inFlight[kind]; return v; },
            function (e) { delete inFlight[kind]; throw e; });
    inFlight[kind] = p;
    return p;
  }

  function thumbUrl(kind, filename) {
    if (!filename) return null;
    return SOURCES[kind].thumbs + filename;
  }

  /* localize a controlled-vocabulary value via the shared i18n matrix.
     catPath is the matrix category path, e.g. "common.biome", "battlemap.type".
     Fallback: requested locale -> en -> the raw value. matrix may be null. */
  function localizeLabel(matrix, catPath, value, locale) {
    if (!matrix || !matrix.domains) return value;
    var parts = catPath.split('.');
    var cat = matrix.domains[parts[0]] && matrix.domains[parts[0]][parts[1]];
    var term = cat && cat[value];
    if (!term) return value;
    return term[locale] || term.en || value;
  }
  function makeLocalizer(matrix, locale) {
    return function (catPath, value) { return localizeLabel(matrix, catPath, value, locale); };
  }

  /* group all entries of one map release into "scenes" (by name).
     release_dir-first matching: entries carrying properties.release_dir are matched
     EXACTLY against bm_<zero-padded release no>, which kills the cross-content bleed
     the pure leading-number match had (release no 1 also matched tour/CoS keys like
     "1-01_..."). The numeric fallback only applies when no release_dir entry matched. */
  function buildRelease(content, releaseNo) {
    var target = String(releaseNo);
    var rdRx = new RegExp('^bm_0*' + target + '(_|$)');
    var withDir = [];
    var numeric = [];
    Object.keys(content).forEach(function (key) {
      var e = content[key]; var props = e.properties || {};
      var entry = { key: key, name: e.name || key, props: props };
      var rd = props.release_dir ? String(props.release_dir) : '';
      if (rd) {
        if (rdRx.test(rd)) withDir.push(entry);
      } else if (releaseOf(key, props.pack) === target) {
        numeric.push(entry);
      }
    });
    var entries = withDir.length ? withDir : numeric;
    var scenes = {}; var order = [];
    var biomes = {}; var adventures = {}; var grids = {};
    var counts = { Battlemap: 0, Scenery: 0 };
    entries.forEach(function (e) {
      var p = e.props;
      if (p.type) counts[p.type] = (counts[p.type] || 0) + 1;
      (p.biom || []).forEach(function (b) { if (b && b !== 'None') biomes[b] = 1; });
      (p.adventure || []).forEach(function (a) { if (a && a !== 'None') adventures[a] = 1; });
      if (p.grid) grids[p.grid] = 1;
      var sceneName = e.name;
      if (!scenes[sceneName]) { scenes[sceneName] = { name: sceneName, types: {}, thumb: null, pack: p.pack }; order.push(sceneName); }
      var sc = scenes[sceneName];
      if (p.type) sc.types[p.type] = 1;
      if (!sc.thumb && p.thumbnail) sc.thumb = thumbUrl('battlemap', p.thumbnail);
    });
    return {
      releaseNo: String(releaseNo),
      entryCount: entries.length,
      sceneCount: order.length,
      counts: counts,
      biomes: Object.keys(biomes).sort(),
      adventures: Object.keys(adventures).sort(),
      grids: Object.keys(grids),
      scenes: order.map(function (n) { return scenes[n]; })
    };
  }

  function chipRow(label, values) {
    if (!values.length) return '';
    return '<div class="pk-chiprow"><span class="pk-chiplabel">' + esc(label) + '</span>' +
      values.map(function (v) { return '<span class="pk-chip">' + esc(v) + '</span>'; }).join('') + '</div>';
  }

  /* map baked full-res scene images (custom.bm_scene_images = [{name,slug,url}]) by a normalized key */
  function slugify(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }
  function buildHiresMap(sceneImages) {
    var map = {};
    if (Array.isArray(sceneImages)) {
      sceneImages.forEach(function (it) {
        if (!it || !it.bm) return;
        var entry = { bm: it.bm, sc: it.sc || null };
        if (it.slug) map[slugify(it.slug)] = entry;
        if (it.name) map[slugify(it.name)] = entry;
      });
    }
    return map;
  }
  var lightboxEl = null;
  function openLightbox(url, alt) {
    if (!lightboxEl) {
      lightboxEl = document.createElement('div');
      lightboxEl.className = 'pk-lightbox';
      lightboxEl.addEventListener('click', function () { lightboxEl.classList.remove('open'); lightboxEl.innerHTML = ''; });
      document.body.appendChild(lightboxEl);
    }
    lightboxEl.innerHTML = '<img src="' + esc(url) + '" alt="' + esc(alt || '') + '">';
    lightboxEl.classList.add('open');
  }

  function renderBattlemapRelease(host, rel, loc, hires, S) {
    loc = loc || function (cat, v) { return v; };
    hires = hires || {};
    S = S || {};
    function sv(k, d) { return S[k] || d; } // localized UI label with English fallback
    var stats = [];
    if (rel.counts.Battlemap) stats.push(rel.counts.Battlemap + ' ' + (rel.counts.Battlemap === 1 ? sv('bm_one', 'battlemap') : sv('bm_other', 'battlemaps')));
    if (rel.counts.Scenery) stats.push(rel.counts.Scenery + ' ' + (rel.counts.Scenery === 1 ? sv('sc_one', 'scenery') : sv('sc_other', 'sceneries')));
    var html = '';
    html += '<div class="pk-stats">' + stats.map(function (s) { return '<span>' + esc(s) + '</span>'; }).join('') + '</div>';
    html += chipRow(sv('biomes', 'Biomes'), rel.biomes.map(function (b) { return loc('common.biome', b); }));
    html += chipRow(sv('adventures', 'Adventures'), rel.adventures.map(function (a) { return loc('battlemap.adventure', a); }));
    function zoomImg(url, alt) {
      return '<img class="pk-zoom-img" data-hires="' + esc(url) + '" src="' + esc(url) + '" alt="' + esc(alt) + '" loading="lazy">';
    }
    html += '<div class="pk-scenes">';
    rel.scenes.forEach(function (sc) {
      var types = Object.keys(sc.types).map(function (t) { return loc('battlemap.type', t); }).join(' + ');
      // Prefer the baked full-res stills (Shopify CDN, permanent). Battlemap + Scenery are shown as a
      // pair (same name) so buyers see they belong together; else fall back to the catalog thumbnail.
      var pair = hires[slugify(sc.name)] || null;
      var imgs;
      if (pair) {
        imgs = zoomImg(pair.bm, sc.name);
        if (pair.sc) imgs += zoomImg(pair.sc, sc.name);
      } else if (sc.thumb) {
        imgs = '<img src="' + esc(sc.thumb) + '" alt="' + esc(sc.name) + '" loading="lazy">';
      } else {
        imgs = '<div class="pk-ph"></div>';
      }
      html += '<figure class="pk-scene' + (pair && pair.sc ? ' pk-pair' : '') + '">' +
        '<div class="pk-imgs">' + imgs + '</div>' +
        '<figcaption><span class="pk-scene-name">' + esc(sc.name) + '</span>' +
        (types ? '<span class="pk-scene-type">' + esc(types) + '</span>' : '') + '</figcaption></figure>';
    });
    html += '</div>';
    html += '<p class="pk-foot">' + esc(sv('extras', 'Plus the extras that ship in every pack: rendered handouts, a cinematic intro, ambience and sound effects, all bundled in one download.')) + '</p>';
    host.innerHTML = html;
    // Click a high-res image to open it full size.
    var zooms = host.querySelectorAll('.pk-zoom-img');
    for (var i = 0; i < zooms.length; i++) {
      (function (im) {
        im.style.cursor = 'zoom-in';
        im.addEventListener('click', function () { openLightbox(im.getAttribute('data-hires'), im.alt); });
      })(zooms[i]);
    }
  }

  /* synthetic release object from the generated bm_scene_images pairs, used when the
     public DB has no trustworthy match for the linked release number */
  function relFromSceneImages(sceneImages) {
    var scenes = [];
    var bm = 0, sc = 0;
    (sceneImages || []).forEach(function (it) {
      if (!it || !it.name) return;
      bm++;
      if (it.sc) sc++;
      var types = { Battlemap: 1 };
      if (it.sc) types.Scenery = 1;
      scenes.push({ name: it.name, types: types, thumb: null, pack: null });
    });
    return {
      releaseNo: '',
      entryCount: scenes.length,
      sceneCount: scenes.length,
      counts: { Battlemap: bm, Scenery: sc },
      biomes: [],
      adventures: [],
      grids: [],
      scenes: scenes
    };
  }

  function initPackContents() {
    var linkEl = document.getElementById('beneos-pack-link');
    var host = document.getElementById('beneos-pack-contents');
    if (!linkEl || !host) return;
    var link;
    try { link = JSON.parse(linkEl.textContent); } catch (e) { return; }
    if (!link || !link.kind) return;

    var locale = link.locale || 'en';
    var S = (link.strings && typeof link.strings === 'object') ? link.strings : {};
    function sv(k, d) { return S[k] || d; }
    if (link.kind === 'battlemap') {
      if (!link.release) { host.innerHTML = '<p class="pk-empty">' + esc(sv('none', 'No release linked yet.')) + '</p>'; return; }
      host.innerHTML = '<p class="pk-loading">' + esc(sv('loading', 'Loading pack contents…')) + '</p>';
      // load the release DB and the i18n matrix in parallel; matrix is best-effort
      Promise.all([
        loadDb('battlemap'),
        (locale === 'en') ? Promise.resolve(null) : loadDb('i18n').catch(function () { return null; })
      ]).then(function (res) {
        var content = res[0], matrix = res[1];
        var rel = buildRelease(content, link.release);
        if (!rel.entryCount) {
          // No trustworthy DB match: render from the generated scene images (ground
          // truth, watermarked Shopify CDN) instead of guessing, else keep the empty note.
          if (Array.isArray(link.sceneImages) && link.sceneImages.length) {
            renderBattlemapRelease(host, relFromSceneImages(link.sceneImages), makeLocalizer(matrix, locale), buildHiresMap(link.sceneImages), S);
          } else {
            host.innerHTML = '<p class="pk-empty">' + esc(sv('no_scenes', 'No scenes found for this release.')) + '</p>';
          }
          return;
        }
        renderBattlemapRelease(host, rel, makeLocalizer(matrix, locale), buildHiresMap(link.sceneImages), S);
      }).catch(function () {
        host.innerHTML = '<p class="pk-empty">' + esc(sv('unavailable', 'Pack contents are temporarily unavailable.')) + '</p>';
      });
    }
  }

  window.BeneosDB = { load: loadDb, releaseOf: releaseOf, buildRelease: buildRelease, thumbUrl: thumbUrl, localizeLabel: localizeLabel };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPackContents);
  else initPackContents();
})();
